import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { reportLabels } from '../../constants/reportLabels';
import { useDownloadReport, useReports } from '../../hooks/useReports';
import ReportHistoryPager from './ReportHistoryPager';
import ReportHistoryTable from './ReportHistoryTable';

interface ReportHistoryDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
}

const TITLE_ID = 'report-history-dialog-title';

const ReportHistoryDialog = ({ projectId, open, onClose }: ReportHistoryDialogProps) => {
  const { reports, total, isPending, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage, isFetchNextPageError } = useReports(
    projectId,
    open,
  );
  const download = useDownloadReport(projectId);
  const downloadingId = download.isPending ? download.variables?.id ?? null : null;

  const renderContent = () => {
    if (isError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {reportLabels.retry_button}
            </Button>
          }
        >
          {reportLabels.history_error}
        </Alert>
      );
    }
    if (isPending) {
      return (
        <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3, justifyContent: 'center' }}>
          <CircularProgress size={24} aria-hidden />
          <Typography>{reportLabels.history_loading}</Typography>
        </Box>
      );
    }
    if (!reports.length) {
      return (
        <Typography role="status" sx={{ py: 3, textAlign: 'center' }} color="text.secondary">
          {reportLabels.history_empty}
        </Typography>
      );
    }
    return (
      <>
        <ReportHistoryTable reports={reports} downloadingId={downloadingId} onDownload={(report) => download.mutate(report)} />
        <ReportHistoryPager
          shown={reports.length}
          total={total}
          hasMore={hasNextPage}
          isLoading={isFetchingNextPage}
          isError={isFetchNextPageError}
          onLoadMore={() => fetchNextPage()}
        />
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={TITLE_ID} fullWidth maxWidth="lg">
      <DialogTitle id={TITLE_ID}>{reportLabels.history_title}</DialogTitle>
      <IconButton aria-label={reportLabels.close_button} onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default ReportHistoryDialog;
